// Voix d'une scène. Trois narrateurs, même contrat :
//   - la voix ATTEND L'IMAGE : avant chaque phrase, `mayStart(i)` (le lecteur
//     répond oui quand l'horloge de la scène est au début étiré de la phrase,
//     c.-à-d. quand les étapes de la précédente sont finies et lues) ;
//   - voix du navigateur (`speechSynthesis`) : une phrase = un énoncé
//     (contourne la coupure des longs énoncés de Chrome), un silence entre
//     deux phrases (`SENTENCE_PAUSE_MS`, compté par le calendrier) ; `onend` absent →
//     repli sur une durée estimée ; pause = annuler la phrase et la reprendre
//     au début (la pause est peu fiable sur Android) ;
//   - voix enregistrée : un seul <audio> réutilisé (garde le déverrouillage
//     iOS), la phrase courante suit le temps de lecture ; les silences du
//     début et de la fin de la prise sont sautés (`offsetMs`, `endMs`) ;
//   - aucune (son coupé, ou pas de voix) : le lecteur suit son horloge.
//
// iOS n'autorise la parole et le son qu'après un geste : `unlock()` doit être
// appelé DANS le gestionnaire du clic « Lecture ».

import { estimateSpeechMs, SENTENCE_PAUSE_MS } from '@jeraisonne/animation';

export interface NarrationCallbacks {
  /** la voix commence la phrase `index` */
  onSentence: (index: number) => void;
  /** la narration de la scène est finie */
  onEnd: () => void;
  /** la voix ne marche pas sur cet appareil : le lecteur passe aux sous-titres seuls */
  onFail?: () => void;
  /** lecture coupée de l'extérieur (appel entrant, casque débranché) : le lecteur se met en pause */
  onInterrupt?: () => void;
  /** la phrase `index` peut-elle commencer ? (non : la voix attend l'image, sans rien dire) */
  mayStart?: (index: number) => boolean;
}

/** Intervalle auquel une voix qui attend l'image redemande si elle peut parler (ms). */
export const VOICE_WAIT_POLL_MS = 60;

export interface SceneNarration {
  /** phrases à dire (`say`, sinon `text`) */
  sentences: string[];
  /** voix enregistrée de la scène */
  audio?: RecordedVoice | undefined;
}

/**
 * Voix enregistrée : débuts des phrases comptés depuis `offsetMs` (silence
 * du début rogné), fin à `endMs` (silence de la fin rogné ; absent : fin du
 * fichier).
 */
export interface RecordedVoice {
  src: string;
  sentenceStartsMs: number[];
  offsetMs?: number | undefined;
  endMs?: number | undefined;
}

export interface Narrator {
  readonly kind: 'browser' | 'audio';
  unlock(): void;
  /** démarre la scène à la phrase `from` */
  start(narration: SceneNarration, from: number, cb: NarrationCallbacks): void;
  pause(): void;
  resume(): void;
  stop(): void;
  /** libère l'appareil (écouteurs) au démontage du lecteur */
  dispose?(): void;
  /** voix enregistrée : instant DANS LE FICHIER et lecture en cours (bouche de l'avatar) */
  voiceTime?(): { fileMs: number; playing: boolean } | null;
}

// ------------------------------------------------------- voix du navigateur

/** Voix françaises préférées, de la plus naturelle à la plus courante. */
const PREFERRED_VOICES = [
  /google français/i,
  /amélie|amelie/i,
  /thomas/i,
  /audrey/i,
  /marie/i,
  /denise/i,
  /henri/i,
  /julie/i,
  /paul/i,
];

export function pickFrenchVoice(
  voices: readonly SpeechSynthesisVoice[],
): SpeechSynthesisVoice | null {
  const fr = voices.filter((v) => /^fr([-_]FR)?$/i.test(v.lang) || /^fr[-_]/i.test(v.lang));
  const frFR = fr.filter((v) => /^fr[-_]FR$/i.test(v.lang));
  const pool = frFR.length ? frFR : fr;
  for (const re of PREFERRED_VOICES) {
    const v = pool.find((x) => re.test(x.name));
    if (v) return v;
  }
  return pool.find((v) => v.localService) ?? pool[0] ?? null;
}

export function hasSpeechSynthesis(): boolean {
  return (
    typeof window !== 'undefined' &&
    'speechSynthesis' in window &&
    typeof window.SpeechSynthesisUtterance === 'function'
  );
}

export class BrowserNarrator implements Narrator {
  readonly kind = 'browser' as const;
  private synth: SpeechSynthesis;
  private voice: SpeechSynthesisVoice | null = null;
  private narration: SceneNarration | null = null;
  private cb: NarrationCallbacks | null = null;
  private index = 0;
  private paused = false;
  /** jeton : un énoncé annulé ne déclenche pas la suite */
  private token = 0;
  private fallback: ReturnType<typeof setTimeout> | null = null;

  private readonly onVoices = () => {
    this.voice = pickFrenchVoice(this.synth.getVoices());
  };

  constructor(private readonly rate = 1) {
    this.synth = window.speechSynthesis;
    this.voice = pickFrenchVoice(this.synth.getVoices());
    // Chrome charge la liste des voix après coup
    this.synth.addEventListener?.('voiceschanged', this.onVoices);
  }

  dispose(): void {
    this.stop();
    this.synth.removeEventListener?.('voiceschanged', this.onVoices);
  }

  unlock(): void {
    try {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      this.synth.speak(u);
    } catch {
      /* rien : la première phrase réessaiera */
    }
  }

  start(narration: SceneNarration, from: number, cb: NarrationCallbacks): void {
    this.stop();
    this.narration = narration;
    this.cb = cb;
    this.index = from;
    this.paused = false;
    this.speakCurrent();
  }

  private speakCurrent(): void {
    const n = this.narration;
    const cb = this.cb;
    if (!n || !cb) return;
    if (this.index >= n.sentences.length) {
      cb.onEnd();
      return;
    }
    // la voix attend que l'image ait fini la phrase précédente (et laissé la lire)
    if (cb.mayStart && !cb.mayStart(this.index)) {
      const waitToken = ++this.token;
      this.clearFallback();
      this.fallback = setTimeout(() => {
        if (waitToken === this.token && !this.paused) this.speakCurrent();
      }, VOICE_WAIT_POLL_MS);
      return;
    }
    const token = ++this.token;
    const text = n.sentences[this.index]!;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = this.voice?.lang ?? 'fr-FR';
    if (this.voice) u.voice = this.voice;
    u.rate = this.rate;
    const next = () => {
      if (token !== this.token || this.paused) return;
      // nouveau jeton : un second signal de fin (repli ET `onend`) ne fait rien
      const gapToken = ++this.token;
      this.clearFallback();
      this.index++;
      if (this.index >= n.sentences.length) {
        this.speakCurrent();
        return;
      }
      // respiration entre deux phrases (annulée par une pause : jeton changé)
      this.fallback = setTimeout(() => {
        if (gapToken === this.token && !this.paused) this.speakCurrent();
      }, SENTENCE_PAUSE_MS);
    };
    const startedAt = Date.now();
    u.onend = next;
    u.onerror = (e) => {
      if (token !== this.token) return;
      const reason = (e as SpeechSynthesisErrorEvent).error;
      // annulation : rien à faire ; échec immédiat : pas de voix utilisable
      if (reason === 'interrupted' || reason === 'canceled') return;
      if (Date.now() - startedAt < 400 && this.cb?.onFail) {
        this.clearFallback();
        this.token++;
        this.cb.onFail();
        return;
      }
      next();
    };
    cb.onSentence(this.index);
    this.cancelIfBusy();
    this.synth.speak(u);
    // `onend` jamais reçu (certaines WebView) : on passe à la suite quand même
    this.clearFallback();
    this.fallback = setTimeout(next, estimateSpeechMs(text, this.rate) * 1.6 + 1500);
  }

  private clearFallback(): void {
    if (this.fallback !== null) clearTimeout(this.fallback);
    this.fallback = null;
  }

  pause(): void {
    this.paused = true;
    this.token++;
    this.clearFallback();
    this.cancelIfBusy();
  }

  /** `cancel` juste avant `speak` fait parfois sauter l'énoncé (Chrome) : seulement si besoin */
  private cancelIfBusy(): void {
    if (this.synth.speaking || this.synth.pending) this.synth.cancel();
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.speakCurrent(); // la phrase reprend à son début
  }

  stop(): void {
    this.token++;
    this.paused = false;
    this.clearFallback();
    this.narration = null;
    this.cb = null;
    this.cancelIfBusy();
  }
}

// --------------------------------------------------------- voix enregistrée

export class AudioNarrator implements Narrator {
  readonly kind = 'audio' as const;
  private readonly el: HTMLAudioElement;
  private cb: NarrationCallbacks | null = null;
  private starts: number[] = [];
  private offsetMs = 0;
  private endMs: number | null = null;
  /** la scène est dite (fin du fichier ou fin utile) */
  private done = false;
  private current = -1;
  /** pause demandée par le lecteur (une autre pause vient de l'extérieur) */
  private pausing = false;
  /** phrase au bord de laquelle la voix attend l'image */
  private waiting: number | null = null;
  private waitTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.el = new Audio();
    this.el.preload = 'auto';
    this.el.addEventListener('timeupdate', this.onTime);
    this.el.addEventListener('ended', this.onEnded);
    this.el.addEventListener('error', this.onError);
    this.el.addEventListener('pause', this.onPause);
  }

  voiceTime(): { fileMs: number; playing: boolean } | null {
    if (!this.cb || this.done) return null;
    return { fileMs: this.el.currentTime * 1000, playing: !this.el.paused };
  }

  unlock(): void {
    // lecture muette dans le geste : iOS autorise ensuite le son de cet élément
    this.el.muted = true;
    this.pausing = true;
    void this.el.play()?.catch(() => undefined);
    this.el.pause();
    this.el.muted = false;
  }

  start(narration: SceneNarration, from: number, cb: NarrationCallbacks): void {
    if (!narration.audio) {
      cb.onEnd();
      return;
    }
    this.clearWait();
    this.cb = cb;
    this.starts = narration.audio.sentenceStartsMs;
    this.offsetMs = narration.audio.offsetMs ?? 0;
    this.endMs = narration.audio.endMs ?? null;
    this.done = false;
    this.current = -1;
    if (this.el.src !== narration.audio.src) this.el.src = narration.audio.src;
    this.seek(this.starts[from] ?? 0);
    this.play(cb);
    this.onTime();
  }

  /** place la lecture à `ms` du début utile de la voix */
  private seek(ms: number): void {
    this.el.currentTime = (this.offsetMs + ms) / 1000;
  }

  private play(cb: NarrationCallbacks): void {
    this.pausing = false;
    void this.el.play()?.catch((e: unknown) => {
      // AbortError : interrompue par une pause demandée, rien à faire
      if (this.cb !== cb || (e as { name?: string })?.name === 'AbortError') return;
      cb.onFail?.();
    });
  }

  private onTime = (): void => {
    if (!this.cb || this.done) return;
    const ms = this.el.currentTime * 1000 - this.offsetMs;
    // fin utile atteinte : le silence d'après n'est pas joué
    if (this.endMs !== null && ms >= this.endMs) {
      this.finish();
      return;
    }
    let k = 0;
    this.starts.forEach((s, i) => {
      if (s <= ms + 1) k = i;
    });
    if (k !== this.current) {
      // la phrase suivante attend l'image : pause au bord, reprise quand elle peut commencer
      if (k > this.current && this.current >= 0 && this.cb.mayStart && !this.cb.mayStart(k)) {
        if (this.waiting === null) {
          this.waiting = k;
          this.pausing = true;
          this.el.pause();
          this.seek(this.starts[k] ?? 0);
          this.pollWait();
        }
        return;
      }
      this.current = k;
      this.cb.onSentence(k);
    }
  };

  private pollWait(): void {
    const cb = this.cb;
    if (!cb || this.waiting === null) return;
    this.waitTimer = setTimeout(() => {
      this.waitTimer = null;
      if (this.cb !== cb || this.waiting === null) return;
      if (cb.mayStart?.(this.waiting) === false) {
        this.pollWait();
        return;
      }
      const k = this.waiting;
      this.waiting = null;
      this.current = k;
      cb.onSentence(k);
      this.play(cb);
    }, VOICE_WAIT_POLL_MS);
  }

  private clearWait(): void {
    if (this.waitTimer !== null) clearTimeout(this.waitTimer);
    this.waitTimer = null;
    this.waiting = null;
  }

  private onEnded = (): void => {
    this.finish();
  };

  private finish(): void {
    if (this.done) return;
    this.done = true;
    this.clearWait();
    if (!this.el.paused) {
      this.pausing = true;
      this.el.pause();
    }
    this.cb?.onEnd();
  }

  private onError = (): void => {
    // fichier introuvable, coupure réseau : on passe aux sous-titres seuls
    this.cb?.onFail?.();
  };

  private onPause = (): void => {
    if (this.pausing || this.el.ended || this.done || !this.cb) return;
    this.cb.onInterrupt?.();
  };

  pause(): void {
    this.pausing = true;
    if (this.waitTimer !== null) clearTimeout(this.waitTimer);
    this.waitTimer = null;
    this.el.pause();
  }

  resume(): void {
    // finie : ne pas la rejouer depuis le début
    if (!this.cb || this.el.ended || this.done) return;
    // elle attendait l'image : elle recommence à attendre
    if (this.waiting !== null) {
      this.pollWait();
      return;
    }
    this.play(this.cb);
  }

  stop(): void {
    this.clearWait();
    this.cb = null;
    this.pausing = true;
    this.el.pause();
  }

  dispose(): void {
    this.stop();
    this.el.removeAttribute('src');
    this.el.removeEventListener('timeupdate', this.onTime);
    this.el.removeEventListener('ended', this.onEnded);
    this.el.removeEventListener('error', this.onError);
    this.el.removeEventListener('pause', this.onPause);
  }
}
